import { themeRawValues } from '@/utils/styleUtils';
import { ChartTooltipWrapper } from '@/components/charts/ChartTooltip';
import { useState } from 'react';
import { TopPlayersPercentageListTooltipContent } from './TopPlayersPercentageListTooltipContent';
import { DevicesPercentageListTooltipContent } from './DevicesPercentageListTooltipContent';
import { formatPercentageTwoDp } from '@/utils/numberFormattingUtils';
import { getDisplayLabel, TOP_PLAYER_CATEGORY } from '../topPlayerCategories';

const getDisplayCategoryForLegend = (category: string) => {
  switch (category) {
    case TOP_PLAYER_CATEGORY.podcastPlayerApps:
    case TOP_PLAYER_CATEGORY.webPodcastPlayers:
      return 'Podcast players';
    default:
      return category;
  }
};

export const PercentagesList = ({
  data,
  legend,
  legendLabels,
  colors,
  usePercentage = true,
  renderTooltipContent,
}: {
  data: Array<{
    label: string | null;
    values: Array<{ label: string; value: number }>;
    total: number;
    plays: number;
  }>;
  legend?: Array<{ category: string; description: string }>;
  legendLabels?: Record<string, string>;
  colors: Record<string, string>;
  usePercentage?: boolean;
  renderTooltipContent?:
    | typeof TopPlayersPercentageListTooltipContent
    | typeof DevicesPercentageListTooltipContent;
}) => {
  return (
    <div className="flex flex-col gap-6">
      <ul className="flex flex-col gap-2 list-none">
        {data.map((item, itemIndex) => {
          const displayLabel = getDisplayLabel(item.label);

          return (
            <li
              key={item.label ?? `unknown-${itemIndex}`}
              className={`grid gap-2 items-center ${
                usePercentage ? 'grid-cols-[8rem_1fr_3rem]' : 'grid-cols-[8rem_1fr_5rem]'
              }`}
            >
              <span className="text-sm font-medium text-right truncate">{displayLabel}</span>
              <ProgressMultiBar
                label={displayLabel}
                percentages={item.values}
                total={item.total}
                plays={item.plays}
                colors={colors}
                renderTooltipContent={renderTooltipContent}
              />
              <span className="text-sm font-medium text-right whitespace-nowrap">
                {usePercentage
                  ? `${formatPercentageTwoDp(item.total)}%`
                  : item.plays.toLocaleString()}
              </span>
            </li>
          );
        })}
      </ul>
      {legend && (
        <div className="flex flex-row gap-4 justify-center">
          {legend.map((item) => (
            <div key={item.category} className="flex flex-row gap-2 items-center text-textMedGrey">
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: colors[item.category] ?? themeRawValues.colors.textMedGrey,
                }}
              />
              <span className="text-xs font-medium  whitespace-nowrap">
                {legendLabels?.[item.category] ?? getDisplayCategoryForLegend(item.category)}
                <br />
                {item.description && (
                  <span className="text-xs font-normal  whitespace-nowrap">{item.description}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProgressMultiBar = ({
  label,
  percentages = [],
  total,
  plays,
  colors,
  renderTooltipContent,
}: {
  label: string;
  percentages: Array<{ label: string; value: number }>;
  total: number;
  plays: number;
  colors: Record<string, string>;
  renderTooltipContent?:
    | typeof TopPlayersPercentageListTooltipContent
    | typeof DevicesPercentageListTooltipContent
    | null;
}) => {
  const [tooltip, setTooltip] = useState<{
    label: string;
    value: number;
    x: number;
  } | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;

    let accumulatedWidth = 0;
    const containerWidth = rect.width;

    const hoveredSegment = percentages.find(() => {
      const segWidth = containerWidth;
      accumulatedWidth += segWidth;
      return offsetX <= accumulatedWidth;
    });

    if (hoveredSegment && renderTooltipContent !== null) {
      setTooltip({
        label: hoveredSegment.label,
        value: hoveredSegment.value,
        x: offsetX,
      });
    } else {
      setTooltip(null);
    }
  };

  return (
    <div className="relative w-full flex flex-row items-center">
      <div
        role="progressbar"
        aria-valuenow={total}
        aria-valuemin={0}
        aria-valuemax={100}
        className={`h-[8px] min-w-0 w-full bg-primary/10 rounded-full my-1 overflow-hidden flex flex-row flex-shrink-0`}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        {percentages.map((percentage) => (
          <div
            key={percentage.label}
            className="bg-primary flex-shrink-0 first:rounded-l-full last:rounded-r-full"
            style={{
              height: 8,
              width: `${percentage.value.toString()}%`,
              backgroundColor: `${colors?.[percentage.label] ?? themeRawValues.colors.textMedGrey}`,
            }}
          />
        ))}
      </div>
      {tooltip && renderTooltipContent && (
        <div
          className="absolute z-[1000] pointer-events-none"
          style={{
            left: tooltip.x,
            bottom: '100%',
            transform: 'translateX(-50%) translateY(-8px)',
          }}
        >
          <ChartTooltipWrapper className="w-64 border-[#F0F0F9] bg-[#FAFAFA] text-textDarkGrey">
            {(() => {
              const TooltipComponent = renderTooltipContent;
              return (
                <TooltipComponent
                  label={label}
                  value={total}
                  plays={plays ?? 0}
                  percentages={percentages}
                />
              );
            })()}
          </ChartTooltipWrapper>
        </div>
      )}
    </div>
  );
};
